import React, { useState, useEffect } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import SimCardImage from 'assets/img/numbers/settings/sim-card-image.svg';
import NumberInfo from 'views/Numbers/Details/common/NumberInfo';
import Card from 'components/Card/Card';
import PinPukRow from './PinPukRow';
import { CustomerAccount } from 'utils/api/models/accounts/customerAccountResponse';
import { getProductCharacteristic } from 'views/Numbers/helpers/getters';
import styles from 'assets/jss/material-dashboard-pro-react/views/settingsSubpageStyle';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import { ResourceInfo } from 'utils/api/models/products/customerProducts';
import { SimService } from 'services/sim/sim';
import { finalize } from 'rxjs/operators';
import { throwError } from 'rxjs';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import QrCode from './QrCode';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IQrResponse {
	qrCode?: string;
	qrLink?: string;
	serialNumber?: string;
}
interface ICheckPinPukDialogProps {
	isOpen: boolean;
	hideDialog: () => void;
	phoneNumber: string;
	accountDetails: string;
	customerAccount: CustomerAccount;
	resourceInfo: ResourceInfo | null;
	isEsim?: boolean;
}

const CheckPinPukDialog = ({
	isOpen,
	hideDialog,
	phoneNumber,
	accountDetails,
	customerAccount,
	resourceInfo,
	isEsim,
}: ICheckPinPukDialogProps) => {
	const classes = useStyles();
	const pinPukCardClasses = classNames({
		[classes.cardBasicStyle]: true,
		[classes.pinPukCard]: true,
	});

	const [esimQrData, setEsimQrData] = useState<IQrResponse | null | undefined>(null);
	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		if (isEsim) {
			setLoading(true);
			SimService.getSimDetails(phoneNumber, null)
				.pipe(
					finalize(() => {
						setLoading(false);
					})
				)
				.subscribe(
					({ response }) => {
						if (response) {
							setEsimQrData({
								qrCode: parseQrCode(response?.qrCode),
								qrLink: response?.url ? `${response?.url} ${response?.activationCode}` : '',
								serialNumber: response?.serialNumber,
							});
						}
					},
					(error) => throwError(error)
				);
		}
	}, [isEsim, phoneNumber]);

	const parseQrCode = (data: string) => {
		return data.substring(10, data.length - 4);
	};

	return (
		<CustomDialog
			isOpen={isOpen}
			hideDialog={hideDialog}
			header={translate('numbers.settings.sim.details')}
			loading={loading || isEsim === undefined}
			dialogBody={
				<div>
					<NumberInfo phoneNumber={phoneNumber} accountDetails={accountDetails} />
					<ConditionalRender
						show={!!esimQrData && !!isEsim}
						onTrue={() => (
							<QrCode
								phoneNumber={phoneNumber}
								setLoader={setLoading}
								esimQrData={esimQrData}
								loader={loading}
								isEsim={isEsim}
							/>
						)}
					/>
					<ConditionalRender
						show={!isEsim}
						onTrue={() => (
							<Card className={pinPukCardClasses}>
								<img src={SimCardImage} alt="Sim card" />
								<div>
									<PinPukRow
										label={translate('numbers.settings.pin1')}
										value={getProductCharacteristic(customerAccount, 'PIN1')}
									/>
									<PinPukRow
										label={translate('numbers.settings.puk1')}
										value={getProductCharacteristic(customerAccount, 'PUK1')}
									/>
									<PinPukRow
										label={translate('numbers.settings.pin2')}
										value={getProductCharacteristic(customerAccount, 'PIN2')}
									/>
									<PinPukRow
										label={translate('numbers.settings.puk2')}
										value={getProductCharacteristic(customerAccount, 'PUK2')}
									/>
									<PinPukRow
										label={translate('numbers.settings.sim')}
										value={resourceInfo?.serialNo || ''}
									/>
								</div>
							</Card>
						)}
					/>
				</div>
			}
		/>
	);
};

export default CheckPinPukDialog;
